import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as projectTypeService from '../services/projectTypeService';
import * as requirementGenerationService from '../services/requirementGenerationService';
import * as projectService from '../services/projectService';
import * as userService from '../services/userService';
import type { ProjectType } from '../services/projectTypeService';
import type { GeneratedRequirement } from '../services/requirementGenerationService';
import type { User } from '../services/userService';

type WizardStep = 1 | 2 | 3 | 4;
type RequirementSetupOption = 'manual' | 'ai' | 'import';

interface EditableRequirement extends GeneratedRequirement {
  id: string; // Temporary ID for tracking during edit
  included: boolean; // Whether to include in final project
}

interface ProjectFormData {
  name: string;
  project_code: string;
  description: string;
  in_scope: string;
  out_scope: string;
  project_type_id: string;
  start_date: string;
  end_date: string;
  status: 'draft' | 'active';
}

/**
 * New Project Creation Wizard
 *
 * Step 1: Basic project information (name, description, dates, type)
 * Step 2: Requirements setup (Manual, AI Assisted, or Import)
 * Step 3: Team member selection
 * Step 4: Review and finalize requirements
 */
export function NewProjectWizard(): React.ReactElement {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [projectTypesLoading, setProjectTypesLoading] = useState(true);

  // Step 1: Project form data
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    project_code: '',
    description: '',
    in_scope: '',
    out_scope: '',
    project_type_id: '',
    start_date: '',
    end_date: '',
    status: 'draft'
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ProjectFormData, string>>>({});

  // Step 2: Requirements setup
  const [requirementOption, setRequirementOption] = useState<RequirementSetupOption | null>(null);
  const [seedingSize, setSeedingSize] = useState<'small' | 'medium' | 'large'>('small');
  const [generatingRequirements, setGeneratingRequirements] = useState(false);
  const [generatedRequirements, setGeneratedRequirements] = useState<GeneratedRequirement[]>([]);
  const [requirementSource, setRequirementSource] = useState<'ai' | 'template' | null>(null);
  const [generationError, setGenerationError] = useState<string>('');

  // Step 3: Team member selection
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string>('');

  // Step 4: Review and edit requirements
  const [editableRequirements, setEditableRequirements] = useState<EditableRequirement[]>([]);

  // Project creation state
  const [creatingProject, setCreatingProject] = useState(false);
  const [creationError, setCreationError] = useState<string>('');

  // Load project types on mount
  useEffect(() => {
    loadProjectTypes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate project code when project name changes (debounced)
  useEffect(() => {
    // Only generate if there's a project name with at least 4 characters
    if (!formData.name || formData.name.trim().length < 4) {
      return;
    }

    // Debounce the generation to avoid too many API calls
    const timeoutId = setTimeout(() => {
      generateProjectCodeFromName(formData.name);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.name]);

  // Load company users when step 3 is reached
  useEffect(() => {
    if (currentStep === 3 && companyUsers.length === 0) {
      loadCompanyUsers();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const loadProjectTypes = async () => {
    if (!token) return;

    try {
      const types = await projectTypeService.listProjectTypes({ is_active: true }, token);
      setProjectTypes(types);
    } catch (error) {
      console.error('Error loading project types:', error);
    } finally {
      setProjectTypesLoading(false);
    }
  };

  const loadCompanyUsers = async () => {
    if (!token) return;

    setUsersLoading(true);
    setUsersError('');

    try {
      const response = await userService.listUsers(
        { status: 'active', limit: 1000 },
        token
      );
      // Filter out current user from the list (they're automatically added as owner)
      const otherUsers = response.users.filter(u => u.id !== user?.id);
      setCompanyUsers(otherUsers);
    } catch (error: any) {
      console.error('Error loading company users:', error);
      setUsersError(error.message || 'Failed to load company users');
    } finally {
      setUsersLoading(false);
    }
  };

  const generateProjectCodeFromName = async (projectName: string) => {
    if (!token) return;

    try {
      const generatedCode = await projectService.generateProjectCode(token, projectName);
      setFormData(prev => ({ ...prev, project_code: generatedCode }));
    } catch (error) {
      console.error('Error generating project code:', error);
      // If generation fails, user can still manually enter a code
    }
  };

  // Validate Step 1 form
  const validateStep1 = (): boolean => {
    const errors: Partial<Record<keyof ProjectFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Project name is required';
    }

    if (!formData.project_code.trim()) {
      errors.project_code = 'Project code is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Project description is required';
    }

    if (!formData.project_type_id) {
      errors.project_type_id = 'Project type is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Step 1: Next button
  const handleStep1Next = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  // Handle Step 2: Requirement option selection
  const handleRequirementOptionSelect = async (option: RequirementSetupOption) => {
    setRequirementOption(option);
    setGenerationError('');

    if (option === 'manual') {
      // Manual mode: Proceed to team member selection, then create project without requirements
      setCurrentStep(3);
    } else if (option === 'ai') {
      // AI mode: Show seeding size selector (generation happens when user clicks "Generate Requirements" button)
      // Don't generate immediately - let user select seeding size first
    } else if (option === 'import') {
      // Import mode: Placeholder for future implementation
      setGenerationError('Import functionality coming soon!');
    }
  };

  // Generate requirements using AI with template fallback
  const generateRequirements = async () => {
    if (!token) return;

    setGeneratingRequirements(true);
    setGenerationError('');

    try {
      const selectedProjectType = projectTypes.find(pt => pt.id === formData.project_type_id);
      if (!selectedProjectType) {
        throw new Error('Project type not found');
      }

      const result = await requirementGenerationService.generateRequirements(
        {
          projectType: selectedProjectType.name,
          projectName: formData.name,
          projectDescription: formData.description,
          inScope: formData.in_scope || undefined,
          outScope: formData.out_scope || undefined,
          startDate: formData.start_date || undefined,
          endDate: formData.end_date || undefined,
          seedingSize
        },
        token
      );

      setGeneratedRequirements(result.requirements);
      setRequirementSource(result.source);

      // Convert to editable format for later review
      const editable: EditableRequirement[] = result.requirements.map((req, index) => ({
        ...req,
        id: `temp-${Date.now()}-${index}`,
        included: true
      }));
      setEditableRequirements(editable);

      // Stay on Step 2 to show generated requirements
      // User will manually click "Next" to proceed to team selection

      if (result.error) {
        setGenerationError(result.error);
      }
    } catch (error: any) {
      console.error('Error generating requirements:', error);
      setGenerationError(error.response?.data?.error || error.message || 'Failed to generate requirements');
      setGeneratedRequirements([]);
    } finally {
      setGeneratingRequirements(false);
    }
  };

  // Create project without requirements (manual mode)
  const createProjectWithoutRequirements = async () => {
    if (!token) return;

    setCreatingProject(true);
    setCreationError('');

    try {
      const project = await projectService.createProject(
        {
          name: formData.name,
          project_code: formData.project_code,
          description: formData.description,
          in_scope: formData.in_scope || undefined,
          out_scope: formData.out_scope || undefined,
          project_type_id: formData.project_type_id,
          start_date: formData.start_date || undefined,
          end_date: formData.end_date || undefined,
          status: formData.status
        },
        token
      );

      // Add selected team members to the project
      const projectId = project.id;
      for (const userId of selectedTeamMembers) {
        try {
          await projectService.addProjectMember(
            projectId,
            { user_id: userId, role: 'contributor' },
            token
          );
        } catch (memberError: any) {
          console.error(`Error adding team member ${userId}:`, memberError);
          // Continue adding other members even if one fails
        }
      }

      // Navigate to project detail page
      navigate(`/projects/${projectId}`);
    } catch (error: any) {
      console.error('Error creating project:', error);
      setCreationError(error.response?.data?.error || error.message || 'Failed to create project');
      setCreatingProject(false);
    }
  };

  // Toggle requirement inclusion
  const toggleRequirementInclusion = (id: string) => {
    setEditableRequirements(reqs =>
      reqs.map(req => (req.id === id ? { ...req, included: !req.included } : req))
    );
  };

  // Remove requirement
  const removeRequirement = (id: string) => {
    setEditableRequirements(reqs => reqs.filter(req => req.id !== id));
  };

  // Create project with requirements
  const handleCreateProject = async () => {
    if (!token) return;

    setCreatingProject(true);
    setCreationError('');

    try {
      // Get only included requirements
      const includedReqs = editableRequirements.filter(req => req.included);

      // Prepare project data
      const projectData: projectService.CreateProjectInput = {
        name: formData.name,
        project_code: formData.project_code,
        description: formData.description,
        in_scope: formData.in_scope || undefined,
        out_scope: formData.out_scope || undefined,
        project_type_id: formData.project_type_id || undefined,
        status: formData.status,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined
      };

      // Prepare requirements data (remove temporary IDs and included field)
      const requirementsData = includedReqs.map(req => ({
        title: req.title,
        description: req.description,
        acceptance_criteria: req.acceptance_criteria,
        requirement_type: req.requirement_type,
        priority: req.priority,
        category: req.category
      }));

      // Create project with requirements in single transaction
      const result = await projectService.createProjectWithRequirements(
        {
          project: projectData,
          requirements: requirementsData
        },
        token
      );

      // Add selected team members to the project
      const projectId = result.project.id;
      for (const userId of selectedTeamMembers) {
        try {
          await projectService.addProjectMember(
            projectId,
            { user_id: userId, role: 'contributor' },
            token
          );
        } catch (memberError: any) {
          console.error(`Error adding team member ${userId}:`, memberError);
          // Continue adding other members even if one fails
        }
      }

      // Navigate to the newly created project
      navigate(`/projects/${projectId}`);
    } catch (error: any) {
      console.error('Error creating project with requirements:', error);
      setCreationError(error.message || 'Failed to create project');
      setCreatingProject(false);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center">
        {/* Step 1 */}
        <div className="flex items-center">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep === 1
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                : 'bg-green-500 text-white'
            }`}
          >
            {currentStep > 1 ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className="text-sm font-semibold">1</span>
            )}
          </div>
          <span className={`ml-2 text-sm font-medium ${currentStep === 1 ? 'text-gray-900 dark:text-gray-50' : 'text-gray-500'}`}>
            Project Info
          </span>
        </div>

        {/* Connector */}
        <div className="mx-4 w-12 h-1 bg-gray-300"></div>

        {/* Step 2 */}
        <div className="flex items-center">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep === 2
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                : currentStep > 2
                ? 'bg-green-500 text-white'
                : 'bg-gray-300 text-gray-600'
            }`}
          >
            {currentStep > 2 ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className="text-sm font-semibold">2</span>
            )}
          </div>
          <span className={`ml-2 text-sm font-medium ${currentStep === 2 ? 'text-gray-900 dark:text-gray-50' : 'text-gray-500'}`}>
            Requirements
          </span>
        </div>

        {/* Connector */}
        <div className="mx-4 w-12 h-1 bg-gray-300"></div>

        {/* Step 3 */}
        <div className="flex items-center">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep === 3
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                : currentStep > 3
                ? 'bg-green-500 text-white'
                : 'bg-gray-300 text-gray-600'
            }`}
          >
            {currentStep > 3 ? (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <span className="text-sm font-semibold">3</span>
            )}
          </div>
          <span className={`ml-2 text-sm font-medium ${currentStep === 3 ? 'text-gray-900 dark:text-gray-50' : 'text-gray-500'}`}>
            Team Members
          </span>
        </div>

        {/* Connector */}
        <div className="mx-4 w-12 h-1 bg-gray-300"></div>

        {/* Step 4 */}
        <div className="flex items-center">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full ${
              currentStep === 4
                ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white'
                : 'bg-gray-300 text-gray-600'
            }`}
          >
            <span className="text-sm font-semibold">4</span>
          </div>
          <span className={`ml-2 text-sm font-medium ${currentStep === 4 ? 'text-gray-900 dark:text-gray-50' : 'text-gray-500'}`}>
            Review & Finalize
          </span>
        </div>
      </div>
    </div>
  );

  // Render Step 1: Project Information
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-4">Project Information</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Provide basic details about your project. This information will help us generate relevant requirements.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Project Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-3 py-2 border ${
              formErrors.name ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            placeholder="e.g., Customer Portal"
          />
          {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
        </div>

        {/* Project Code */}
        <div>
          <label htmlFor="project_code" className="block text-sm font-medium text-gray-700 mb-1">
            Project Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="project_code"
            value={formData.project_code}
            readOnly
            className={`w-full px-3 py-2 border ${
              formErrors.project_code ? 'border-red-500' : 'border-gray-300'
            } rounded-md bg-gray-50 text-gray-700 cursor-not-allowed`}
            placeholder="Auto-generated from project name"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Automatically generated from the project name</p>
          {formErrors.project_code && <p className="mt-1 text-sm text-red-600">{formErrors.project_code}</p>}
        </div>
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Project Description <span className="text-red-500">*</span>
        </label>
        <textarea
          id="description"
          rows={4}
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className={`w-full px-3 py-2 border ${
            formErrors.description ? 'border-red-500' : 'border-gray-300'
          } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
          placeholder="Describe the project goals, scope, and key features..."
        />
        {formErrors.description && <p className="mt-1 text-sm text-red-600">{formErrors.description}</p>}
        <p className="mt-1 text-sm text-gray-500">
          Provide a detailed description to help generate more relevant requirements.
        </p>
      </div>

      {/* In Scope */}
      <div>
        <label htmlFor="in_scope" className="block text-sm font-medium text-gray-700 mb-1">
          In Scope
        </label>
        <textarea
          id="in_scope"
          rows={3}
          value={formData.in_scope}
          onChange={(e) => setFormData({ ...formData, in_scope: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="What is included in this project's scope..."
        />
        <p className="mt-1 text-sm text-gray-500">
          Define what is explicitly included in this project (used in BRD generation).
        </p>
      </div>

      {/* Out of Scope */}
      <div>
        <label htmlFor="out_scope" className="block text-sm font-medium text-gray-700 mb-1">
          Out of Scope
        </label>
        <textarea
          id="out_scope"
          rows={3}
          value={formData.out_scope}
          onChange={(e) => setFormData({ ...formData, out_scope: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="What is explicitly excluded from this project's scope..."
        />
        <p className="mt-1 text-sm text-gray-500">
          Define what is explicitly excluded from this project (used in BRD generation).
        </p>
      </div>

      {/* Project Type */}
      <div>
        <label htmlFor="project_type_id" className="block text-sm font-medium text-gray-700 mb-1">
          Project Type <span className="text-red-500">*</span>
        </label>
        <select
          id="project_type_id"
          value={formData.project_type_id}
          onChange={(e) => setFormData({ ...formData, project_type_id: e.target.value })}
          className={`w-full px-3 py-2 border ${
            formErrors.project_type_id ? 'border-red-500' : 'border-gray-300'
          } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
          disabled={projectTypesLoading}
        >
          <option value="">Select a project type...</option>
          {projectTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
              {type.description && ` - ${type.description.substring(0, 50)}...`}
            </option>
          ))}
        </select>
        {formErrors.project_type_id && <p className="mt-1 text-sm text-red-600">{formErrors.project_type_id}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Start Date */}
        <div>
          <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="start_date"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            className={`w-full px-3 py-2 border ${
              formErrors.start_date ? 'border-red-500' : 'border-gray-300'
            } rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500`}
          />
          {formErrors.start_date && <p className="mt-1 text-sm text-red-600">{formErrors.start_date}</p>}
        </div>

        {/* End Date */}
        <div>
          <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-1">
            Target End Date
          </label>
          <input
            type="date"
            id="end_date"
            value={formData.end_date}
            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            min={formData.start_date}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleStep1Next}
          className="px-6 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
        >
          Next: Requirements Setup
        </button>
      </div>
    </div>
  );

  // Render Step 2: Requirements Setup
  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-4">Requirements Setup</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Choose how you want to set up requirements for your project.
        </p>
      </div>

      {!requirementOption && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Manual Entry */}
          <button
            onClick={() => handleRequirementOptionSelect('manual')}
            className="p-6 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-indigo-500 hover:shadow-md transition-all text-left"
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">Manual Entry</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Start with an empty project and add requirements manually as you go.
            </p>
          </button>

          {/* AI Assisted */}
          <button
            onClick={() => handleRequirementOptionSelect('ai')}
            className="p-6 border-2 border-indigo-500 rounded-lg shadow-md hover:shadow-lg transition-all text-left bg-gradient-to-br from-indigo-50 to-blue-50"
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-indigo-100 mb-4">
              <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">
              AI Assisted
              <span className="ml-2 px-2 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full">
                Recommended
              </span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Generate 25-30 personalized requirements using AI based on your project details.
            </p>
          </button>

          {/* Import */}
          <button
            onClick={() => handleRequirementOptionSelect('import')}
            className="p-6 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-indigo-500 hover:shadow-md transition-all text-left opacity-50 cursor-not-allowed"
            disabled
          >
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-4">
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-2">
              Import
              <span className="ml-2 px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">
                Coming Soon
              </span>
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Import requirements from CSV, Excel, or other project management tools.</p>
          </button>
        </div>
      )}

      {/* AI Seeding Size Selection */}
      {requirementOption === 'ai' && !generatingRequirements && generatedRequirements.length === 0 && (
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-50 mb-3">
              Select Seeding Size
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Small */}
              <button
                onClick={() => setSeedingSize('small')}
                className={`p-4 border-2 rounded-lg transition-all text-left ${
                  seedingSize === 'small'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-300 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-gray-50">Small</h4>
                  {seedingSize === 'small' && (
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">~25 requirements</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Quick start for smaller projects</p>
              </button>

              {/* Medium */}
              <button
                onClick={() => setSeedingSize('medium')}
                className={`p-4 border-2 rounded-lg transition-all text-left ${
                  seedingSize === 'medium'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-300 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-gray-50">Medium</h4>
                  {seedingSize === 'medium' && (
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">~50 requirements</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Balanced for most projects</p>
              </button>

              {/* Large */}
              <button
                onClick={() => setSeedingSize('large')}
                className={`p-4 border-2 rounded-lg transition-all text-left ${
                  seedingSize === 'large'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-300 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-gray-50">Large</h4>
                  {seedingSize === 'large' && (
                    <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">~100 requirements</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Comprehensive for complex projects</p>
              </button>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={generateRequirements}
              className="inline-flex items-center gap-2 px-6 py-3 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 shadow-md hover:shadow-lg transition-all"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Generate Requirements
            </button>
          </div>
        </div>
      )}

      {/* AI Generation Loading State */}
      {requirementOption === 'ai' && generatingRequirements && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-lg font-medium text-gray-900 dark:text-gray-50">Generating Requirements...</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">This may take a few minutes</p>
        </div>
      )}

      {/* AI Generation Results */}
      {requirementOption === 'ai' && !generatingRequirements && generatedRequirements.length > 0 && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-800">
                  Successfully Generated {generatedRequirements.length} Requirements
                </h4>
                <p className="mt-1 text-sm text-green-700">
                  Source: {requirementSource === 'ai' ? 'AI Generation' : 'Template Fallback'}
                </p>
                {generationError && <p className="mt-1 text-sm text-green-700">{generationError}</p>}
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400">
            Requirements have been generated and will be reviewed in the next step. You'll be able to edit, remove, or add
            more requirements before finalizing your project.
          </p>
        </div>
      )}

      {/* Error State */}
      {generationError && generatedRequirements.length === 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="ml-3">
              <h4 className="text-sm font-medium text-red-800">Generation Failed</h4>
              <p className="mt-1 text-sm text-red-700">{generationError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <button
          onClick={() => {
            setCurrentStep(1);
            setRequirementOption(null);
            setGeneratedRequirements([]);
            setGenerationError('');
          }}
          className="px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>

        {requirementOption === 'ai' && generatedRequirements.length > 0 && (
          <button
            onClick={() => setCurrentStep(3)}
            className="px-6 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
          >
            Next: Select Team Members
          </button>
        )}
      </div>
    </div>
  );

  // Helper function to get role color
  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      company_admin: 'bg-indigo-100 text-indigo-800',
      project_manager: 'bg-blue-100 text-blue-800',
      contributor: 'bg-gray-100 text-gray-800',
      platform_admin: 'bg-purple-100 text-purple-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  // Render Step 3: Team Member Selection
  const renderStep3 = () => {
    const toggleUserSelection = (userId: string) => {
      setSelectedTeamMembers(prev =>
        prev.includes(userId)
          ? prev.filter(id => id !== userId)
          : [...prev, userId]
      );
    };

    const selectAllUsers = () => {
      setSelectedTeamMembers(companyUsers.map(u => u.id));
    };

    const deselectAllUsers = () => {
      setSelectedTeamMembers([]);
    };

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-4">Select Team Members</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Choose team members to add to this project. You (the project owner) will be automatically added. Team members will be assigned as Contributors.
          </p>
        </div>

        {/* Error messages */}
        {creationError && (
          <div className="p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800">{creationError}</p>
          </div>
        )}

        {usersError && (
          <div className="p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800">{usersError}</p>
          </div>
        )}

        {/* Loading state */}
        {usersLoading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-lg font-medium text-gray-900 dark:text-gray-50">Loading team members...</p>
          </div>
        )}

        {/* Team members list */}
        {!usersLoading && companyUsers.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">
                Available Team Members ({selectedTeamMembers.length} selected)
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={selectAllUsers}
                  className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-full transition-colors"
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllUsers}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded-full transition-colors"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {companyUsers.map(user => (
                <div
                  key={user.id}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedTeamMembers.includes(user.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                  onClick={() => toggleUserSelection(user.id)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedTeamMembers.includes(user.id)}
                      onChange={() => toggleUserSelection(user.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">
                        {user.first_name} {user.last_name}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{user.email}</div>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {user.role.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Empty state */}
        {!usersLoading && companyUsers.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p className="mt-4 text-sm text-gray-500">
              No other team members available. You can add team members later from the project page.
            </p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            onClick={() => setCurrentStep(2)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          {/* Show different button based on requirement option */}
          {requirementOption === 'manual' ? (
            <button
              onClick={createProjectWithoutRequirements}
              disabled={creatingProject}
              className={`inline-flex items-center px-6 py-2 border border-transparent rounded-full text-sm font-medium text-white ${
                creatingProject
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'
              }`}
            >
              {creatingProject ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Project...
                </>
              ) : (
                <>
                  Create Project
                  <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={() => setCurrentStep(4)}
              className="inline-flex items-center px-6 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            >
              Next: Review Requirements
              <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };

  // Render Step 4: Review and Edit Requirements
  const renderStep4 = () => {
    const includedReqs = editableRequirements.filter(req => req.included);

    return (
      <div className="space-y-6">
        {/* Error message */}
        {creationError && (
          <div className="p-4 rounded-md bg-red-50 border border-red-200">
            <p className="text-sm text-red-800">{creationError}</p>
          </div>
        )}

        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-50 mb-2">Review Requirements</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Review and customize the generated requirements. You can edit, remove, or add new requirements before creating your project.
          </p>

          {/* Source indicator */}
          {requirementSource && (
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
              requirementSource === 'ai' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {requirementSource === 'ai' ? (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  AI Generated
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Template Based
                </>
              )}
            </div>
          )}

          {generationError && (
            <div className="mt-3 p-3 rounded-md bg-yellow-50 border border-yellow-200">
              <p className="text-sm text-yellow-800">{generationError}</p>
            </div>
          )}
        </div>

        {/* Requirements List */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50">
              Requirements ({includedReqs.length} selected)
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentStep(3)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-full text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <button
                onClick={handleCreateProject}
                disabled={includedReqs.length === 0 || creatingProject}
                className={`inline-flex items-center px-6 py-2 border border-transparent rounded-full text-sm font-medium text-white ${
                  includedReqs.length === 0 || creatingProject
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700'
                }`}
              >
                {creatingProject ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    Create Project
                    <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Requirements table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      <input
                        type="checkbox"
                        checked={includedReqs.length === editableRequirements.length}
                        onChange={(e) => {
                          const allIncluded = e.target.checked;
                          setEditableRequirements(reqs =>
                            reqs.map(req => ({ ...req, included: allIncluded }))
                          );
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {editableRequirements.map((req) => (
                    <tr key={req.id} className={!req.included ? 'bg-gray-50 opacity-60' : ''}>
                      <td className="px-3 py-4">
                        <input
                          type="checkbox"
                          checked={req.included}
                          onChange={() => toggleRequirementInclusion(req.id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </td>
                      <td className="px-3 py-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-50">{req.title}</div>
                        <div className="text-sm text-gray-500 line-clamp-2">{req.description}</div>
                      </td>
                      <td className="px-3 py-4">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {req.requirement_type}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          req.priority === 'critical' ? 'bg-red-100 text-red-800' :
                          req.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          req.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {req.priority}
                        </span>
                      </td>
                      <td className="px-3 py-4">
                        <button
                          onClick={() => removeRequirement(req.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {editableRequirements.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No requirements yet.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 dark:from-indigo-800 dark:via-blue-800 dark:to-blue-600 rounded-lg shadow-lg mb-6 p-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
          >
            ← Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-white">Create New Project</h1>
          <p className="mt-2 text-blue-100">Follow the steps below to set up your project</p>
        </div>

        {/* Wizard Content */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-8">
          {renderStepIndicator()}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>
      </div>
    </Layout>
  );
}
